import { AppText } from "@toothfairy/shared-ui";
import AppSecureTextInput from "@toothfairy/shared-ui/AppSecureTextInput";
import AppSwitch from "@toothfairy/shared-ui/AppSwitch";
import AppTheme from "@toothfairy/shared-ui/AppTheme";
import React, { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { View } from "react-native";
import {
  DEFAULT_AUTO_RECHARGE,
  MIN_RECHARGE_USD,
  formatUsd,
} from "../../Constants/billing";

// AutoRechargeControl: toggle + threshold (recharge when balance < X) + amount.
//
// Pure editor: sliders edit a LOCAL draft and report it via onDraftChange.
// Saving is handled by the parent (one shared Save button). Per-field seeds
// (initialValue) are only re-synced from `value` when it differs from the last
// reported draft, so the parent echoing the same draft back does NOT reseed
// mid-drag (which would loop AppSecureTextInput's reset).
const AutoRechargeControl = ({ value, onDraftChange, hasPaymentMethod = true, style }) => {
  const { t } = useTranslation();
  const { currentTheme } = AppTheme.useTheme();
  const saved = value || DEFAULT_AUTO_RECHARGE;

  const [draft, setDraft] = useState(saved);
  // Seeds drive the sliders' initialValue (what the thumbs show).
  const [thresholdSeed, setThresholdSeed] = useState(
    Number(saved?.thresholdUsd) || 1,
  );
  const [amountSeed, setAmountSeed] = useState(
    Number(saved?.amountUsd) || MIN_RECHARGE_USD,
  );
  // Track the last saved value we seeded from, so a reseed only fires when the
  // SAVED value changes (load/save) — never when the draft diverges (toggle/drag).
  const lastSeeded = useRef(JSON.stringify(saved));

  useEffect(() => {
    const incoming = JSON.stringify(value || DEFAULT_AUTO_RECHARGE);
    if (incoming !== lastSeeded.current) {
      const next = value || DEFAULT_AUTO_RECHARGE;
      setDraft(next);
      setThresholdSeed(Number(next?.thresholdUsd) || 1);
      setAmountSeed(Number(next?.amountUsd) || MIN_RECHARGE_USD);
      lastSeeded.current = incoming;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  const report = (next) => {
    // eslint-disable-next-line no-console
    console.log("[AutoRechargeControl] report", next);
    onDraftChange?.(next);
  };

  return (
    <View
      style={[
        {
          padding: 12,
          backgroundColor: currentTheme?.theme?.softBackgroundColor,
          borderRadius: 10,
          marginTop: 8,
        },
        style,
      ]}
    >
      <View
        style={{
          flexDirection: "row",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <AppText style={{ fontWeight: "bold", flex: 1, flexWrap: "wrap" }}>
          {t("credits.autoRecharge.title")}
        </AppText>
        <AppSwitch
          isTicked={!!draft?.enabled && hasPaymentMethod}
          onChange={(enabled) => {
            if (enabled && !hasPaymentMethod) return;
            const next = { ...DEFAULT_AUTO_RECHARGE, ...draft, enabled };
            setDraft(next);
            report(next);
          }}
          disabled={!hasPaymentMethod}
          wrapperStyle={{ paddingVertical: 0 }}
        />
      </View>
      <AppText
        style={{
          fontSize: 12,
          color:
            currentTheme?.theme?.secondary_font_color ||
            currentTheme?.theme?.font_color,
          marginTop: 4,
        }}
      >
        {t("credits.autoRecharge.description")}
      </AppText>
      {!hasPaymentMethod && (
        <AppText
          style={{
            fontSize: 12,
            color: currentTheme?.theme?.orange,
            marginTop: 8,
          }}
        >
          {t("credits.autoRecharge.noPaymentMethod", {
            defaultValue:
              "Add a payment method to enable auto-recharge — it charges your saved card when credits run low.",
          })}
        </AppText>
      )}

      {draft?.enabled && (
        <View style={{ marginTop: 12 }}>
          <AppText style={{ fontSize: 13, marginBottom: 4 }}>
            {t("credits.autoRecharge.thresholdLabel")}
          </AppText>
          <AppSecureTextInput
            key={`threshold-${thresholdSeed}`}
            inputType="SLIDER"
            showTextInputLabel={false}
            isEditable
            showEditButtons={false}
            initialValue={thresholdSeed}
            onSliderValueChange={(v) => {
              const next = {
                ...DEFAULT_AUTO_RECHARGE,
                ...draft,
                thresholdUsd: Math.max(1, Number(v) || 0),
              };
              setDraft(next);
              report(next);
            }}
            minimumValue={1}
            maximumValue={100}
            step={1}
            unitLabel={t("credits.units.usd", { defaultValue: "$" })}
            wrapperStyle={{ width: "100%" }}
            coreViewStyle={{ minHeight: 40 }}
          />
          <AppText style={{ fontSize: 13, marginTop: 10, marginBottom: 4 }}>
            {t("credits.autoRecharge.amountLabel")}
          </AppText>
          <AppSecureTextInput
            key={`amount-${amountSeed}`}
            inputType="SLIDER"
            showTextInputLabel={false}
            isEditable
            showEditButtons={false}
            initialValue={amountSeed}
            onSliderValueChange={(v) => {
              const next = {
                ...DEFAULT_AUTO_RECHARGE,
                ...draft,
                amountUsd: Math.max(MIN_RECHARGE_USD, Number(v) || 0),
              };
              setDraft(next);
              report(next);
            }}
            minimumValue={MIN_RECHARGE_USD}
            maximumValue={500}
            step={5}
            unitLabel={t("credits.units.usd", { defaultValue: "$" })}
            wrapperStyle={{ width: "100%" }}
            coreViewStyle={{ minHeight: 40 }}
          />
          <AppText
            style={{
              fontSize: 11,
              color: currentTheme?.theme?.secondary_font_color,
              marginTop: 6,
            }}
          >
            {t("credits.autoRecharge.summary", {
              threshold: formatUsd(draft?.thresholdUsd || 0),
              amount: formatUsd(draft?.amountUsd || 0),
            })}
          </AppText>
        </View>
      )}
    </View>
  );
};

export default AutoRechargeControl;
